import React, { useCallback, useEffect, useRef, useState } from "react";
import type Connection from "../models/Connection";
import { ConnectionType } from "../models/Connection";
import type Page from "../models/Page";
import Point from "../models/Point";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import { eventPointToDrawingArea } from "./GraficElementUtils";
import { ConnectionDisplayModes } from "../State";
import { PagePrintInfo } from "../models/PagePrintInfo";
import Logger from "../Logger";
import type Scene from "../models/Scene";
import ConnectableObject from "../models/ConnectableObject";
import type Button from "../models/Button";
import type Room from "../models/Room";
import type Actor from "../models/Actor";
import { isUiModeExpert, isUiModeUser } from "../uiMode";
import { useRedraw } from "../models/useRerender";
import { noItx } from "../translationItx/itx";
import Channel from "../models/Channel";
import BaseModel from "../models/BaseModel";
import { channelTypeInfo } from "../models/ChannelType";
import { forceShowIcons } from "./ConsumerView";
import Floor from "../models/Floor";

const logger = new Logger();

// Lazily initialized canvas 2D context used exclusively for measuring text metrics.
// Avoids creating a DOM element on every render cycle.
let textMeasureContext: CanvasRenderingContext2D | null = null;

/**
 * Measures the rendered width and height of a given text string at the specified font size.
 * Uses a hidden off-screen canvas for accurate browser metrics.
 * Falls back to an approximation when the canvas API is unavailable (e.g., SSR).
 */
function getTextMetrics(text: string, fontSizePx: number) {
  if (typeof document !== "undefined" && !textMeasureContext) {
    const canvas = document.createElement("canvas");
    textMeasureContext = canvas.getContext("2d");
  }

  if (!textMeasureContext) {
    const fallbackHeight = fontSizePx;
    return {
      width: Math.max(8, text.length * fontSizePx * 0.6),
      height: fallbackHeight,
      ascent: fallbackHeight * 0.8,
      descent: fallbackHeight * 0.2,
    };
  }

  textMeasureContext.font = `${fontSizePx}px sans-serif`;
  const metrics = textMeasureContext.measureText(text);
  const ascent = metrics.actualBoundingBoxAscent || fontSizePx * 0.8;
  const descent = metrics.actualBoundingBoxDescent || fontSizePx * 0.2;
  const width = Math.max(8, metrics.width);

  return {
    width,
    height: ascent + descent,
    ascent,
    descent,
  };
}

/**
 * Positions and rotates a directional arrow polygon at the midpoint of an SVG path.
 * The arrow is oriented along the tangent direction of the path at that midpoint,
 * giving a visual indication of the connection's signal flow direction.
 */
function updateMidArrow(path: SVGPathElement, arrow: SVGPolygonElement) {
  const total = path.getTotalLength();
  if (!isFinite(total) || total <= 0) return;

  // Punkt in der Mitte
  const midLen = total / 2;
  const P = path.getPointAtLength(midLen);

  // Tangentenrichtung um die Mitte (kleines Delta links/rechts)
  const delta = Math.max(0.01, total * 0.001); // skaliert mit Pfadlänge
  const P1 = path.getPointAtLength(Math.max(0, midLen - delta));
  const P2 = path.getPointAtLength(Math.min(total, midLen + delta));

  const dx = P2.x - P1.x;
  const dy = P2.y - P1.y;
  const angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;

  // Pfeil nach (x,y) verschieben und in Fahrtrichtung drehen
  // Unser Polygon zeigt standardmäßig nach +X (rechts), daher passt rotate(angleDeg)
  arrow.setAttribute("transform", `translate(${P.x}, ${P.y}) rotate(${angleDeg})`); // no itx
}

/**
 * Renders a single KNX connection (wiring line) between two connectable objects on the page canvas.
 *
 * Supports three visual modes:
 *  - Bezier curve with interactive grab-point manipulation
 *  - Text label (shown instead of a line when `showConnectionTexts` is active)
 *  - Hidden (when filtered by display mode or selection state)
 *
 * The component subscribes to changes on the connection itself and both of its endpoints
 * so it re-renders whenever the topology changes.
 */
export default function ConnectionView(props: {
  mainPage: Page;
  page: Page;
  fontScale: number;
  connection: Connection;
  print?: PagePrintInfo;
}) {
  const { mainPage, page, connection, print } = props;
  var { fontScale } = props;

  const thisElement = useRef<any>(null);
  const refArrow = useRef<SVGPolygonElement>(null);
  const refPath = useRef<SVGPathElement>(null);

  useOnChangeWatcher(connection, (changedProperty, changedObject) => {
    //logger.log("connection" + changedProperty);
    return true;
  });
  useOnChangeWatcher(connection.fromDevice, (changedProperty, changedObject) => {
    //logger.log("connection.fromDevice" + changedProperty);
    return true;
  });
  useOnChangeWatcher(connection.toDevice, (changedProperty, changedObject) => {
    //logger.log("connection.toDevice" + changedProperty);
    return true;
  });
  useOnChangeWatcher(state.settings);

  useOnChangeWatcher(state.selectionManager);

  const redrawTimer = useRef<any>(null);
  const redraw = useRedraw();

  // useOnChangeWatcher(mainPage.currentZoom, (changedProperty, changedObject) => {
  //   updateConnectionAfterPanZoom();
  //   return false;
  // });

  // useOnChangeWatcher(mainPage.currentPan, (changedProperty, changedObject) => {
  //   updateConnectionAfterPanZoom();
  //   return false;
  // });

  // hover: true when the pointer is over the connection line (used to thicken the stroke).
  // grabPoint: the current screen position of the dragged inner waypoint while editing.
  const [hover, setHover] = useState(false);
  const [grabPoint, setGrabPoint] = useState<Point | undefined>();

  useEffect(() => {
    if (refPath.current && refArrow.current) {
      updateMidArrow(refPath.current, refArrow.current);
    }
  });

  const id = connection.id;
  const fromPoint = connection.tmpFromPoint || connection.fromPort(document, mainPage.id)?.point;
  let toPoint = connection.toPort(document, mainPage.id)?.point;

  var pointerEvents: any = "auto";

  if (connection.toDevice == undefined) {
    pointerEvents = "none";
  }

  const handleonPointerMove = useCallback((ev: any) => {
    if (!print) {
      onPointerMove(ev);
    }
  }, []);

  const handleonPointerUp = useCallback((ev: any) => {
    if (!print) {
      onPointerUp(ev);
    }
  }, []);

  if (
    state.settings.customerView &&
    (connection.toDevice?.parent?.className == "Actor" || connection.fromDevice?.parent?.className == "Actor")
  ) {
    return null;
  }

  // Updates the dragged inner waypoint position while the user is actively moving
  // a selected connection. Attached to the window so the pointer can leave the element.
  function onPointerMove(event: React.PointerEvent<SVGElement>) {
    event.stopPropagation();

    if (!print) {
      if (connection.selected) {
        const pLocal = eventPointToDrawingArea(event);
        setGrabPoint(pLocal);
        if (connection.mouseIsDown && connection.innerPoints.length > 0 && pLocal) {
          connection.innerPoints[connection.innerPoints.length - 1] = pLocal;
        }
      }
    }
  }

  // Handles pointer-move events that originate directly on the SVG connection group.
  // Delegates to grab-point tracking when selected, or activates hover highlighting otherwise.
  function onPointerMove1(ev: any) {
    if (!print) {
      if (connection.selected) {
        const pLocal = eventPointToDrawingArea(ev);
        setGrabPoint(pLocal);
      } else {
        setHoverIfactiv(true);
      }
    }
  }

  // Sets the hover state only when text labels are not permanently shown.
  // In text-label mode the hover highlight is suppressed to avoid visual clutter.
  function setHoverIfactiv(value: boolean) {
    if (!state.settings.showConnectionTexts) {
      setHover(value);
    }
  }

  // Begins an interactive connection-path edit when the user clicks a selected connection.
  // Creates an undo snapshot of the inner waypoints array before modifying it,
  // then adds a new waypoint at the click position and registers global move/up listeners.
  function onPointerDown(event: React.PointerEvent<SVGElement>) {
    if (!print) {
      event.stopPropagation();

      if (thisElement.current) {
        thisElement.current.setPointerCapture(event.pointerId);
      }

      if (connection.selected) {
        page.connectionManipulationActive = true;
        // create undoStep

        const newArray = new Array<Point>();
        connection.innerPoints = newArray;
        connection.raiseOnChange();

        const pLocal = eventPointToDrawingArea(event);
        if (pLocal) {
          // only 1 middlepoint for now
          if (connection.innerPoints.length == 0) {
            connection.innerPoints.push(pLocal);
          }

          connection.mouseIsDown = true;
          setGrabPoint(pLocal);

          window.addEventListener("mousemove", handleonPointerMove);
          window.addEventListener("mouseup", handleonPointerUp);
        }
      }
    }
  }

  // Ends the interactive waypoint drag: releases pointer capture, resets drag state,
  // and removes the global event listeners registered in onPointerDown.
  function onPointerUp(event: React.PointerEvent<SVGElement>) {
    event.stopPropagation();

    if (!print) {
      if (thisElement.current) {
        thisElement.current.releasePointerCapture(event.pointerId);
      }

      page.connectionManipulationActive = false;
      connection.mouseIsDown = false;
      window.removeEventListener("mousemove", handleonPointerMove);
      window.removeEventListener("mouseup", handleonPointerUp);
    }
  }

  // Determines whether this connection should be hidden based on the global
  // connection display mode filter (all / channels / scenes / buttons / none).
  // Also lazily migrates legacy connection types to the Multiplexer type.
  function isHiddenViaDisplayMode(): boolean {
    /// only to set old chonnection types to Multiplexer
    const multiplexer = connection.getMultiplexer();
    if (multiplexer) {
      connection.type = ConnectionType.Multiplexer;
    }
    ///
    switch (state.connectionDisplayMode.value) {
      case ConnectionDisplayModes.all:
        return false;

      case ConnectionDisplayModes.channels:
        return connection.type != ConnectionType.Channel && connection.type != ConnectionType.TerminalVia;

      case ConnectionDisplayModes.scenes:
        // check here for Multiplexer
        if (connection.type == ConnectionType.Multiplexer) {
          return false;
        }
        return connection.type != ConnectionType.Scene;

      case ConnectionDisplayModes.buttons:
        // show connection between buttonchannel and multiplexer
        if (connection.type == ConnectionType.Multiplexer && connection.getButtonChannel()) {
          return false;
        }
        return connection.type != ConnectionType.Button;

      case ConnectionDisplayModes.none:
        return true;
      default:
        return true;
    }
  }

  // Returns true when at least one of the connection's two endpoints (or their parent
  // devices) is the currently selected object. Used to keep relevant connections
  // visible even when the display mode would otherwise hide them.
  function checkIfPartnerIsSelected(): boolean {
    function check1(obj: ConnectableObject | undefined): boolean {
      if (!obj) {
        return false;
      }
      if (obj == state.selectionManager.selectedObject) {
        return true;
      }
      if (obj.className == "ButtonChannel" || obj.className == "Channel") {
        return obj.parent == state.selectionManager.selectedObject;
      }
      return false;
    }

    // check if partner is Selected

    if (check1(connection.fromDevice) || check1(connection.toDevice)) {
      return true;
    }

    return false;
  }

  // Builds the array of SVG elements that visually represent the connection line.
  // Produces:
  //  - a visible <path> element with the computed Bezier curve
  //  - an invisible wider hit-test <path> so the user can click the thin line easily
  //  - optionally a mid-path directional arrow (for TerminalVia connections)
  //  - optionally a grab-point circle when the user is dragging a waypoint
  function createCurves(w1: number): Array<any> {
    logger.log(`createCurves ${w1} ${mainPage.currentZoom.value}`);

    w1 = w1 / mainPage.currentZoom.value;

    const w = `${w1}px`;
    const wHitTest = `${10 / mainPage.currentZoom.value}px`;
    const wGrab = `${3 / mainPage.currentZoom.value}px`;

    const svgItems: any = [];

    if (connection.type == ConnectionType.TerminalVia) {
      svgItems.push(
        <g ref={refArrow} id="midArrow" fill={color}>
          <polygon
            points="0,-9 0,9 20,0" // no itx
          />
        </g>,
      );
    }

    svgItems.push(
      <path //visible connection line
        ref={refPath}
        style={{ pointerEvents: pointerEvents }}
        id={id}
        key={id}
        d={d}
        stroke={color}
        strokeWidth={w}
        strokeDasharray={connection.type == ConnectionType.TerminalVia ? "10 5" : undefined}
        markerEnd={connection.type == ConnectionType.TerminalVia ? `url(#triangle)` : undefined}
        fill="none"
      />,
    );
    svgItems.push(
      <path //not visible connection line | for selection of the line
        style={{ pointerEvents: pointerEvents, position: "absolute" }}
        key={id + "b1"} // no itx
        d={d}
        stroke="var(--colorTransparent)"
        strokeWidth={wHitTest}
        fill="none"
      />,
    );
    if (grabPoint) {
      svgItems.push(
        <circle //visualisation of the grab Point
          key={id + "g1"} // no itx
          cx={`${grabPoint.x}`}
          cy={`${grabPoint.y}`}
          r="10"
          strokeWidth={wGrab}
          stroke={color}
          fill={connection.mouseIsDown ? color : "var(--colorTransparent)"}
        />,
      );
    }
    return svgItems;
  }

  // Converts an angle from degrees to radians.
  function toRad(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  if (toPoint && connection.animateFromPoint && connection.animateStartTime) {
    const dt = Date.now() - connection.animateStartTime;

    const runtime = 2000;

    if (dt < runtime) {
      let animationDx = toPoint.x - connection.animateFromPoint.x;
      let animationDy = toPoint.y - connection.animateFromPoint.y;
      animationDx *= dt / runtime;
      animationDy *= dt / runtime;

      toPoint = new Point(connection.animateFromPoint.x + animationDx, connection.animateFromPoint.y + animationDy);
      setTimeout(() => {
        redraw();
      }, 20);
    } else {
      connection.animateFromPoint = undefined;
      connection.animateStartTime = undefined;
    }
  }

  var dx = 0;
  var dy = 0;
  if (toPoint && fromPoint) {
    dx = toPoint.x - fromPoint.x;
    dy = toPoint.y - fromPoint.y;
  }

  var allowToBottom = false;

  function angleFromDxDy(dx: number, dy: number): number {
    // recht oder links
    var a = dx > 0 ? 0 : 180;

    if (Math.abs(dx) < Math.abs(dy)) {
      //  oben oder unten
      if (dy < 0) {
        if (allowToBottom) {
          a = 90;
        }
      } else {
        a = 270;
      }
    }
    return a;
  }

  function calcToAngle(): number | undefined {
    const d = calcToDxDy();
    if (d) {
      const res = angleFromDxDy(-d.x, d.y);
      return res;
    }

    return undefined;
  }

  function calcToDxDy(): Point | undefined {
    if (toPoint && fromPoint) {
      var dx = toPoint.x - fromPoint.x;
      var dy = toPoint.y - fromPoint.y;

      const l = connection.innerPoints.length;
      if (l > 0) {
        dx = toPoint.x - connection.innerPoints[l - 1].x;
        dy = toPoint.y - connection.innerPoints[l - 1].y;
      }

      return new Point(dx, dy);
    }

    return undefined;
  }

  function calcFromAngle(): number | undefined {
    const d = calcFromDxDy();
    if (d) {
      return angleFromDxDy(d.x, -d.y);
    }

    return undefined;
  }

  function calcFromDxDy(): Point | undefined {
    if (toPoint && fromPoint) {
      var dx = toPoint.x - fromPoint.x;
      var dy = toPoint.y - fromPoint.y;

      const l = connection.innerPoints.length;
      if (l > 0) {
        dx = connection.innerPoints[0].x - fromPoint.x;
        dy = connection.innerPoints[0].y - fromPoint.y;
      }

      return new Point(dx, dy);
    }

    return undefined;
  }

  // Main rendering logic wrapped in try/catch to prevent a single bad connection
  // from crashing the entire page canvas.
  //
  // Bezier angle convention:
  //   0°   = right, 45° = bottom-right, 90° = down, 180° = left
  // "smooth == 0" produces a straight line segment at that end instead of a curve.
  try {
    // 0 grad == rechts
    // 45 grad = rechts unten
    // 180 = links

    // "smooth == 0" ergibt am jeweiligen Ende eine Line, anstatt einer Kurve

    var fromSmooth = 100;
    var toSmooth = 100;

    const MIN_SMOOTH = 80;
    const MAX_SMOOTH = 200;

    //////////////////////////////////////////////////////////////

    var fromDir = connection.fromPort(document, mainPage.id)?.dir;
    if (fromDir == undefined) {
      fromDir = calcFromAngle();
    }

    const fromDxDy = calcFromDxDy();
    let dxFrom = Math.min(fromDxDy?.x || 10, fromDxDy?.y || 10);
    if (dxFrom) {
      fromSmooth = Math.min(Math.max(Math.abs(dxFrom), MIN_SMOOTH), MAX_SMOOTH);
    }

    const toDxDy = calcToDxDy();
    let dxTo = Math.min(toDxDy?.x || 10, toDxDy?.y || 10);
    if (dxTo) {
      toSmooth = Math.min(Math.max(Math.abs(dxTo), MIN_SMOOTH), MAX_SMOOTH);
    }

    if (fromDir == undefined) {
      fromDir = 0;
      fromSmooth = 0;
    }

    //////////////////////////////////////////////////////////////
    // calc to angle
    //////////////////////////////////////////////////////////////

    //logger.log(`calc-a1 ${toDir}`);

    var toDir = connection.toPort(document, mainPage.id)?.dir;
    if (toDir == undefined) {
      toDir = calcToAngle();
    }

    //logger.log(`calc-a2 ${toDir}`);

    if (toDir == undefined) {
      toDir = 0;
      toSmooth = 0;
    }

    let showConnectionTexts = state.settings.showConnectionTexts;
    let hideConnectionBecauseConsumerIsOutsideView = false;

    //
    // Hide Consumer-Channel-Connection
    // If Consumer is scrolled left out of the view
    //     and the Channel is keeped inside the View
    //
    // MWE 25.11.2024 (Keep Cabinet inside the view)
    //

    logger.log(`connection A: ${connection.fromDevice?.fullTag}  ${connection.toDevice?.fullTag}`);
    if (
      state.settings.keepCabinetAndScenesInsideView &&
      connection.type == ConnectionType.Channel &&
      toPoint &&
      fromPoint
    ) {
      if (connection.fromDevice?.className == "Channel" && connection.fromDevice?.parent?.className == "Actor") {
        if (toPoint.x < fromPoint.x) {
          hideConnectionBecauseConsumerIsOutsideView = true;
        }
      }
      if (connection.toDevice?.className == "Channel" && connection.toDevice?.parent?.className == "Actor") {
        if (fromPoint.x < toPoint.x) {
          hideConnectionBecauseConsumerIsOutsideView = true;
        }
      }
    }

    if (hideConnectionBecauseConsumerIsOutsideView) {
      return null;
    }

    //////////////////////////////////////////////////////////////

    // logger.log(
    //   `connection  ${connection.fromDevice?.tag}  ${connection.toDevice?.tag} ${connection.isHovered} ${connection.isHighlighted}`,
    // );

    const selectedObjectInPage = mainPage.selectedObjectInPage;
    const partnerIsSelected = checkIfPartnerIsSelected();

    if (selectedObjectInPage) {
      //
      // draw new connections allways (connection.tmpToPoint == undefined)
      //
      if (!partnerIsSelected && connection.tmpToPoint == undefined && !connection.isNew && !connection.selected) {
        return null;
      }
    }

    //
    // hide ActorChannel-Consumer-Connection if uiMode user
    //
    if (isUiModeUser() && connection.type == ConnectionType.Channel && !partnerIsSelected) {
      return null;
    }

    if (fromPoint && toPoint) {
      var w = 2;
      if (connection.selected && !print) {
        w += 3;
      }

      if (hover && !print) {
        w += 4;
      }

      const toTangentPoint = new Point(
        toPoint.x + Math.cos(toRad(toDir)) * toSmooth,
        toPoint.y + Math.sin(toRad(toDir)) * toSmooth,
      );

      const fromTangentPoint = new Point(
        fromPoint.x + Math.cos(toRad(fromDir)) * fromSmooth,
        fromPoint.y + Math.sin(toRad(fromDir)) * fromSmooth,
      );

      const dFrom = `M ${fromPoint.x} ${fromPoint.y} C ` + `${fromTangentPoint.x} ${fromTangentPoint.y} `;

      var dInner = "";

      const pp = new Array<Point>();

      pp.push(fromTangentPoint);
      connection.innerPoints.forEach((p) => {
        pp.push(p);
      });

      pp.push(toTangentPoint);

      const smoothInner = Math.max(Math.min(50, toSmooth / 2), fromSmooth / 2);

      for (let pi = 1; pi < pp.length - 1; pi++) {
        const p = pp[pi];
        var dx1 = pp[pi - 1].x - pp[pi + 1].x;
        var dy1 = pp[pi - 1].y - pp[pi + 1].y;

        const l1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
        dx1 = (dx1 / l1) * smoothInner;
        dy1 = (dy1 / l1) * smoothInner;

        var dx2 = -dx1;
        var dy2 = -dy1;

        dInner += ` ${p.x + dx1} ${p.y + dy1}` + ` ${p.x} ${p.y} ` + ` ${p.x + dx2} ${p.y + dy2} `;
      }

      // if (connection.type == ConnectionType.TerminalVia) {
      //   toPoint.x -= Math.sign(dxTo || 1) * 40 * state.settings.getConsumerScale(mainPage.isFloorPlan);
      // }

      const dTo = ` ${toTangentPoint.x} ${toTangentPoint.y} ` + `${toPoint.x} ${toPoint.y}`;
      var d = dFrom + dInner + dTo;

      // definition of the view of the connection of consumer (all, channels, scenes, button connection)
      var color = connection.color;
      var svgItems: any = [];
      var opacity = 1;
      const connectionIsCurrentlyInDrawingMode = connection.tmpToPoint;

      if (connection.isHovered || connection.highlighted || connection.selected || connectionIsCurrentlyInDrawingMode) {
        svgItems = createCurves(w);
      } else {
        if (isHiddenViaDisplayMode()) {
          return null;
        }

        // define the position from the text on consumer

        const showChannelConnectionsAsText =
          showConnectionTexts || (!isUiModeExpert() && connection.type == ConnectionType.Channel);
        // check if partner is Selected
        if (
          showChannelConnectionsAsText &&
          !connection.highlighted &&
          !connection.isHovered &&
          !(hover && isUiModeExpert()) &&
          !checkIfPartnerIsSelected()
        ) {
          svgItems = drawText(page, mainPage, fromPoint, connection, svgItems, color, fontScale, toPoint);
        } else {
          if (
            state.connectionDisplayMode.value == ConnectionDisplayModes.all &&
            !connection.highlighted &&
            !connection.isHovered &&
            !hover
          ) {
            opacity = 0.3;
          }

          if (!hover && !connection.forbidden) {
            w = 2;
            //
            // Das blau der Taster-Linien ist wesentlich deutlicher zu sehen aus die andern Typen
            // daher stellen wir ihn im "normalen Zustand etwas dünner da zu Angleichung
            //
            if (connection.type == ConnectionType.Button) {
              w = 1;
            }
          }

          if (connection.tmpToPoint) {
            w = 4;
          }

          svgItems = createCurves(w);
        }
      }

      return (
        <g
          ref={thisElement}
          style={{ opacity: opacity }}
          onClick={(ev) => {
            let partner = connection.fromDevice;
            if (partner?.isConsumer) {
              partner = connection.toDevice;
            }
            if (partner?.isConsumer) {
              partner = undefined;
            }

            const highlightConnectdChannel = true;
            if (partner && highlightConnectdChannel) {
              // state.selectionManager.selectedObject = partner;

              state.selectionManager.setSelectedObjectWithoutNavigatorUpdate(connection);

              if (channelTypeInfo(partner.channelType).isSupply) {
                partner = partner.parent as Actor;
              }

              state.selectionManager.clearAdditionalHighlights();
              state.selectionManager.addHeightLightedObject(partner);
            } else {
              state.selectionManager.selectedObject = connection;
            }
            page.resetIsNewOnAllConnections();
          }}
          onPointerUp={(ev) => {
            onPointerUp(ev);
          }}
          onPointerEnter={(ev) => {
            if (!page.connectionManipulationActive && !print) {
              setHoverIfactiv(true);
            }
          }}
          onPointerMove={(ev) => {
            if (!connection.mouseIsDown && !print) {
              onPointerMove1(ev);
            }
          }}
          onPointerDown={(ev: React.PointerEvent<SVGElement>) => {
            if (!print) {
              onPointerDown(ev);
            }
          }}
          onPointerLeave={(ev) => {
            if (!page.connectionManipulationActive && !print) {
              setHoverIfactiv(false);
              setGrabPoint(undefined);
            }
          }}
        >
          {svgItems}
        </g>
      );
    } else if (fromPoint || toPoint) {
      // actor of Channel is not in the same cabinet as room cabinet

      let point = fromPoint || toPoint;
      if (point) {
        let svgItems: any = [];
        let color = connection.color;
        var opacity = 1;

        svgItems = drawText(page, mainPage, point, connection, svgItems, color, fontScale);
        return (
          <g
            style={{ opacity: opacity }}
            onClick={(ev) => {
              state.selectionManager.selectedObject = connection;
            }}
          >
            {svgItems}
          </g>
        );
      }
    } else {
      logger.log(`render Connection ${id} fromPoint || toPoint == null`);
    }
  } catch (ex) {
    logger.error("ConnectionView", ex);
  }

  return null;
}

/**
 * Renders a connection as a text label near its endpoint instead of a bezier line.
 * Used when `showConnectionTexts` is active or when the UI mode does not expose
 * the full wiring diagram (e.g., user/consumer mode for channel connections).
 *
 * Only endpoints that belong to the currently rendered page are labeled
 * (see `isOnVisibleSide`).
 */
function drawText(
  page: Page,
  mainPage: Page,
  fromPoint: Point,
  connection: Connection,
  svgItems: any,
  color: string,

  fontScale: number,
  toPoint?: Point,
) {
  const allowArray = [
    { type: ConnectionType.Scene, className: "Scene" }, // no itx
    { type: ConnectionType.Multiplexer, className: "Multiplexer" },
    { type: ConnectionType.Channel, className: "Channel" }, // no itx
    { type: ConnectionType.Channel, className: "Consumer" }, // no itx
  ];

  // Resolves the human-readable display name and class identifier for a connectable endpoint.
  // The label format differs by object type: Scenes include the scene number, Channels show
  // a cabinet-relative tag, Consumers show a room-relative tag, ButtonChannels show a
  // room-relative tag composed from parent and own tags.
  function getNameAndType(obj: ConnectableObject | undefined): { name: string; className: string } {
    const res = { name: "", className: obj?.className || "" };

    const room = connection.parent?.parent as Room;

    switch (obj?.className) {
      case noItx("Scene"): {
        const scene = obj as Scene;
        res.name = scene?.name + `(${scene?.sceneNumber})`;

        break;
      }
      case "ButtonChannel": {
        const relativeTag = room ? obj?.getRelativeTag(room) : "";
        if (relativeTag) {
          res.name = relativeTag;
        } else {
          const parentTag = obj?.parent?.tagOrName || "";
          const ownTag = obj?.tagOrName || "";
          res.name = [parentTag, ownTag].filter(Boolean).join(".") || obj?.fullTag || "";
        }

        break;
      }
      case noItx("Consumer"): {
        if (room) {
          res.name = obj?.getRelativeTag(room);
        } else {
          res.name = obj?.tagOrName;
        }

        break;
      }
      case noItx("Channel"): {
        let obj1 = obj as BaseModel;
        if (!(obj1 as Channel).knxChannel && obj1.parent) {
          //
          // MWE 9.3.2025 für Kanal ohne KNX-Kanal
          // zeigen wir nur den Tag des Aktos an,
          // da der Tag des Kanals keinerlei Information enthält
          //
          obj1 = obj1.parent as BaseModel;
        }
        if (room.cabinet) {
          res.name = obj1.getRelativeTag(room.cabinet);
        } else {
          res.name = obj.fullTag;
        }
        //
        // zur weitern vereinfachung der Anzeige
        // filtern wir hier auch noch den führenden Betriebsmittel prefix raus
        //
        if (res.name.startsWith("-")) {
          res.name = res.name.substring(1);
        }

        break;
      }
    }
    return res;
  }

  svgItems = [];
  // Builds the SVG label items (background rect + text + connector line) for one endpoint
  // of a connection. Determines text alignment, vertical stacking offset, and whether to
  // show the label at all based on the object type and current UI mode.
  function getSvGItems(
    mainPage: Page,
    obj: ConnectableObject | undefined,
    partnerObj: ConnectableObject | undefined,
    point: Point,
    fontScale: number,
  ) {
    const objNameAndClass = getNameAndType(obj);
    const partnerNameAndClass = getNameAndType(partnerObj);
    let beginTextVar: "start" | "end" | "middle" = "end";
    let directionCon = `left`;
    let rightDirection: "up" | "down" | undefined; // no itx
    let showText = true;

    let xCordLine = point.x;
    let yCordLine = point.y;

    const dx = 15;
    let xCord = point.x - dx;
    let yCord = point.y + 5;

    let alignmentBaseline:
      | "auto"
      | "central" // no itx
      | "inherit"
      | "baseline" // no itx
      | "before-edge" // no itx
      | "text-before-edge" // no itx
      | "middle"
      | "after-edge" // no itx
      | "text-after-edge" // no itx
      | "ideographic" // no itx
      | "alphabetic" // no itx
      | "hanging" // no itx
      | "mathematical" // no itx
      | undefined = "central"; // no itx

    logger.log("fontScale", fontScale);

    let fontSizeScaled = (10 * fontScale) / mainPage.currentZoom.value;

    if (page != mainPage) {
      if (!forceShowIcons(mainPage)) {
        return null;
      }
      fontSizeScaled /= fontScale;
    }

    const bachgroundHeight = 20;

    // Returns the vertical stacking index of this connection among sibling connections
    // that share the same partner class, so multiple labels do not overlap.
    function getPlace(): number {
      let place = 0;
      let alreadySelf = false;
      obj?.connections.sortBy("id").forEach((con) => {
        if (con == connection) {
          alreadySelf = true;
          return;
        }
        if (!alreadySelf) {
          const partner = con.getPartner(obj);
          if (partner?.className == partnerNameAndClass.className) {
            place++;
          }
        }
      });
      return place;
    }
    // When a Button is collapsed, distributes its channel labels vertically by
    // computing a pixel offset from the centre anchor point of the button.
    // Returns undefined when the button is expanded (labels are placed normally).
    function getPlaceButtonChannel(middle: number): number | undefined {
      let res = middle;
      // check if the channels are viewn
      const button = obj?.parent as Button;
      if (button?.collapseMode == 0) {
        // ist eingeklapt
        let channelsConnected = 0;
        let place = 0;
        button.buttonChannels.sortBy("fullTag").forEach((bc) => {
          if (bc.connections.size > 0) {
            channelsConnected++;
          }
          if (bc == obj) {
            place = channelsConnected;
          }
        });
        const halfHeight = bachgroundHeight * 0.5;
        const placeHeight = bachgroundHeight * place;
        res = middle + halfHeight - placeHeight;
      } else {
        return undefined;
      }
      return res;
    }

    // Collects the display names of all partner objects connected to `obj` (or to all
    // channels of its parent Actor when obj is a Channel). Partners that belong to
    // a different room are excluded. Results are joined with " | ".
    function getAllPartnerNames(mainPage: Page): string {
      let res = "";
      let partners = new Array<ConnectableObject>();

      if (obj?.className == "Channel") {
        (obj.parent as Actor)?.channels.forEach((channel) => {
          channel.connections.forEach((con) => {
            const partner = con.getPartner(channel);
            if (partner) {
              partners.push(partner);
            }
          });
        });
      } else {
        obj?.connections.forEach((con) => {
          const partner = con.getPartner(obj);
          if (partner) {
            partners.push(partner);
          }
        });
      }
      partners?.forEach((partner) => {
        const partnerData = getNameAndType(partner);
        const partnerRoom =
          partnerData.className == "Consumer"
            ? partner?.parent
            : partnerData.className == "ButtonChannel"
              ? partner?.parent?.parent
              : undefined;
        if (partnerRoom == connection.parent?.parent) {
          if (res != "") {
            res = res + " | ";
          }
          res = res + partnerData.name;
        }
      });
      return res;
    }
    if (objNameAndClass.className == "Scene" || objNameAndClass.className == "Multiplexer") {
      if (state.selectionManager.selectedObject == obj) {
        showText = false;
      }
      partnerNameAndClass.name = getAllPartnerNames(mainPage);
      beginTextVar = `end`;
      directionCon = `left`;
    } else if (
      (partnerNameAndClass.className == "Scene" && objNameAndClass.className != "ButtonChannel") ||
      (partnerNameAndClass.className == "Multiplexer" && objNameAndClass.className != "ButtonChannel")
    ) {
      beginTextVar = `start`;
      directionCon = `right`;
      rightDirection = "up"; // no itx
    } else if (objNameAndClass.className == "Channel") {
      // if collapsed
      if ((obj?.parent as Actor)?.isCollapsed) {
        partnerNameAndClass.name = getAllPartnerNames(mainPage);
      }
      beginTextVar = `start`;
      directionCon = `right`;
    } else if (partnerNameAndClass.className == "Channel") {
      beginTextVar = `end`;
      directionCon = `left`;
    } else if (objNameAndClass.className == "ButtonChannel") {
      if (partnerNameAndClass.className == "Consumer") {
        partnerNameAndClass.name = getAllPartnerNames(mainPage);
      }
      beginTextVar = `start`;
      directionCon = `right`;
    } else if (partnerNameAndClass.className == "ButtonChannel") {
      beginTextVar = `start`;
      directionCon = `right`;
      rightDirection = "down"; // no itx
    }

    if (directionCon == `left`) {
      if (objNameAndClass.className == "Scene") {
        xCord = point.x - dx;
      } else {
        xCord = point.x - 2 * dx;
      }
      yCord = point.y;
      xCordLine = xCord + 5 / mainPage.currentZoom.value;
    } else {
      // right
      if (objNameAndClass.className == "Channel") {
        xCord = point.x + dx;
      } else {
        xCord = point.x + 2 * dx;
      }
      xCordLine = xCord - 5;
      if (rightDirection) {
        ///// place vor consumer
        alignmentBaseline = "text-after-edge"; // no itx
        if (rightDirection == "up") {
          const dy = bachgroundHeight * getPlace();
          yCord = point.y - dy;
        } else {
          const dy = bachgroundHeight * getPlace() + bachgroundHeight;
          yCord = point.y + dy;
        }
      } else {
        // general place
        yCord = point.y;
        // place for foldet button channels
        if (obj?.className == "ButtonChannel") {
          const newPosition = getPlaceButtonChannel(point.y);
          if (newPosition) {
            yCord = newPosition;
          }
        }
      }
    }
    function getShowTextInConnectionView(): boolean {
      // never show labels directly on button side
      if (objNameAndClass.className == "ButtonChannel") {
        return false;
      }
      return true;
    }

    const showChannelConnectionsAsText = connection.type != ConnectionType.Channel && !isUiModeExpert();

    if (showChannelConnectionsAsText || (showText && getShowTextInConnectionView())) {
      const xxx =
        partnerNameAndClass.name ||
        (partnerObj as ConnectableObject | undefined)?.tagOrName ||
        partnerObj?.fullTag ||
        "";
      if (!xxx) {
        return;
      }
      const fontSizePx = 12;
      const padX = 8;
      const padY = 4;
      const textMetrics = getTextMetrics(xxx, fontSizePx);
      const textWidth = textMetrics.width;
      const textHeight = textMetrics.height;

      let bgX = xCord - padX;
      if (beginTextVar == "end") {
        bgX = xCord - textWidth - padX;
      }

      let textTop = yCord - textMetrics.ascent;
      if (alignmentBaseline == "central") {
        textTop = yCord - textHeight / 2;
      } else if (alignmentBaseline == "text-after-edge" || alignmentBaseline == "after-edge") {
        textTop = yCord - textHeight;
      } else if (alignmentBaseline == "text-before-edge" || alignmentBaseline == "before-edge") {
        textTop = yCord;
      }
      const bgY = textTop - padY;

      svgItems.push(
        <rect
          x={bgX}
          y={bgY}
          width={textWidth + padX * 2}
          height={textHeight + padY * 2}
          fill="white"
          stroke={"white"}
          strokeWidth={`${1 / mainPage.currentZoom.value}px`}
          style={{ pointerEvents: "none" }}
        />,
      );
      svgItems.push(
        <text
          filter="url(#txtBG)" // no itx
          textAnchor={beginTextVar}
          x={xCord}
          y={yCord}
          fill={connection.color}
          fontSize={`12px`}
          alignmentBaseline={alignmentBaseline}
          style={{ pointerEvents: "all", backgroundColor: "white" }}
        >
          {xxx}
        </text>,
      );
      svgItems.push(
        <line //line from consumer
          x1={xCordLine}
          y1={yCordLine}
          x2={point.x}
          y2={point.y}
          strokeWidth={`${1 / mainPage.currentZoom.value}px`}
          stroke={color}
        />,
      );
    }
  }

  // Returns true when `obj` (and its port point) should be labeled on the current page.
  // Scene, Multiplexer and ActorChannel connections are always visible.
  // For Consumer/Channel/ButtonChannel connections the check verifies that
  // the parent Room (or Floor for floor-plan pages) matches the page's parent.
  function isOnVisibleSide(
    obj: ConnectableObject | undefined,
    objPoint: Point | undefined,
    partnerObj: ConnectableObject | undefined,
    partnerPoint: Point | undefined,
  ): boolean {
    if (!obj || !objPoint) {
      return false;
    }

    function getRoomOrFloorParent(
      obj: ConnectableObject,
      roomOrFloor: "Room" | "Floor", // no itx
    ): Room | Floor | undefined {
      let res: Room | Floor | undefined = undefined;
      if (obj.className == "Consumer") {
        if (obj.parent?.className == "Room" && roomOrFloor == "Room") {
          res = obj.parent as Room;
        } else if (obj.parent?.parent?.className == "Floor" && roomOrFloor == "Floor") {
          res = obj.parent?.parent as Floor;
        }
      } else if (obj.className == "Channel" || obj.className == "ButtonChannel") {
        if (obj.parent?.className == "Button") {
          if (obj.parent?.parent?.className == "Room" && roomOrFloor == "Room") {
            res = obj.parent?.parent as Room;
          } else if (obj.parent?.parent?.parent?.className == "Floor" && roomOrFloor == "Floor") {
            res = obj.parent?.parent?.parent as Floor;
          }
        }
      }
      return res;
    }
    // first scene, Multiplexer and ActorChannel connections
    if (
      obj.className == "Scene" ||
      obj.className == "Multiplexer" ||
      partnerObj?.className == "Scene" ||
      partnerObj?.className == "Multiplexer"
    ) {
      return true;
    }
    if (
      (obj.className == "Channel" && obj.parent?.className == "Actor") ||
      (partnerObj?.className == "Channel" && partnerObj.parent?.className == "Actor")
    ) {
      return true;
    }

    // easiest on Room Page
    const roomOrFloorFromObj = getRoomOrFloorParent(
      obj,
      mainPage.isFloorPlan ? "Floor" : "Room", // no itx
    );
    const roomOrFloorFromPartnerObj = getRoomOrFloorParent(
      partnerObj as ConnectableObject,
      mainPage.isFloorPlan ? "Floor" : "Room", // no itx
    );
    if (mainPage.parent == roomOrFloorFromObj && mainPage.parent == roomOrFloorFromPartnerObj) {
      return true;
    }
    return false;
  }

  // Renders the text label for `obj` only when it is on the visible side of the current
  // page. Delegates to getSvGItems which pushes the resulting SVG elements into svgItems.
  function drawIfVisible(
    obj: ConnectableObject | undefined,
    partnerObj: ConnectableObject | undefined,
    objPoint: Point | undefined,
    partnerPoint: Point | undefined,
  ) {
    if (isOnVisibleSide(obj, objPoint, partnerObj, partnerPoint) && objPoint) {
      getSvGItems(mainPage, obj, partnerObj, objPoint, fontScale);
    }
  }

  // Dispatch: decide which endpoint(s) receive a text label based on the class
  // combination of fromDevice / toDevice. Both sides are labeled when the connection
  // crosses between a Consumer and a Channel so both rooms see a reference.
  if (connection.fromDevice?.className == "Consumer") {
    drawIfVisible(connection.fromDevice, connection.toDevice, fromPoint, toPoint);
    if (connection.toDevice?.className == "Channel" && toPoint) {
      drawIfVisible(connection.toDevice, connection.fromDevice, toPoint, fromPoint);
    }
  } else if (connection.toDevice?.className == "Consumer") {
    drawIfVisible(connection.toDevice, connection.fromDevice, toPoint, fromPoint);
    if (connection.fromDevice?.className == "Channel" && fromPoint) {
      drawIfVisible(connection.fromDevice, connection.toDevice, fromPoint, toPoint);
    }
  } else {
    if (connection.fromDevice?.className == "Channel") {
      drawIfVisible(connection.fromDevice, connection.toDevice, fromPoint, toPoint);
    } else if (connection.toDevice?.className == "Channel") {
      drawIfVisible(connection.toDevice, connection.fromDevice, toPoint, fromPoint);
    } else if (connection.fromDevice?.className == "ButtonChannel") {
      drawIfVisible(connection.toDevice, connection.fromDevice, toPoint, fromPoint);
    } else if (connection.toDevice?.className == "ButtonChannel") {
      drawIfVisible(connection.fromDevice, connection.toDevice, fromPoint, toPoint);
    } else if (toPoint) {
      drawIfVisible(connection.fromDevice, connection.toDevice, toPoint, fromPoint);
    }
  }

  return svgItems;
}
